import { useCallback, useMemo, useState } from "react"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import {
  updateImageGallery,
  selectImageGallery,
  updatePDFViewer,
  selectPDFViewer,
} from "@/reducers/overlaysSlice"
import { mapFileUrlToSameOrigin } from "@/components/message/utils/mapFileToSameOrigin"
import {
  DirectImageEntry,
  ImageLinkInfo,
  ImageMetadataOverride,
} from "@/components/photoView/ImageGallery"
import {
  Message,
  DocumentDriverContext,
  MessageDisplayType,
} from "@/reducers/messagesSlice"
import type { CmrDocumentMetadata } from "./useCmrReportData"

export type PreviewImageDoc = {
  url: string
  metadata?: CmrDocumentMetadata
  messageId?: string
  chatJid?: string
  driverJid?: string
  unlinkInfo?: {
    deliveryOrderMessageId: string
    messageRoomJid: string
  }
}

export type PreviewPdfReExtractInfo = {
  messageId: string
  chatJid?: string
  driverJid?: string
  unlinkInfo?: {
    deliveryOrderMessageId: string
    messageRoomJid: string
  }
}

// Stable empty array passed to <ImageGallery messages={...}>; prevents the
// gallery's internal memos from busting on every parent re-render.
const EMPTY_MESSAGES: Message[] = []

/**
 * Tag dispatched onto Redux ImageGallery / PDFViewer state so the default
 * in-chat ImageGallery / PDFViewer instance in ChatHistory skips rendering
 * while this hook owns the open viewer. Without it, two viewers mount in
 * parallel and stacking-order races between dev and prod (the "image
 * shifted left and stuck on first frame" symptom in the search dialog).
 */
const PREVIEW_SOURCE = "cmrReport"

function toDocumentDriverContext(
  ctx: CmrDocumentMetadata["driverContext"],
): DocumentDriverContext | undefined {
  if (!ctx) return undefined
  return {
    id: "",
    to: "",
    from: "",
    messageId: ctx.messageId,
    type: "groupchat",
    timestamp: "",
    plates: ctx.plates,
    location: ctx.location,
    displayType: MessageDisplayType.DocumentDriverContext,
  }
}

export function useCmrDocumentPreview() {
  const dispatch = useAppDispatch()
  const { visible: galleryVisible, externalSource: gallerySource } =
    useAppSelector(selectImageGallery)
  const { visible: pdfVisible, externalSource: pdfSource } =
    useAppSelector(selectPDFViewer)
  const ownsGallery = gallerySource === PREVIEW_SOURCE
  const ownsPdf = pdfSource === PREVIEW_SOURCE

  const [activeDirectImages, setActiveDirectImages] = useState<
    DirectImageEntry[]
  >([])
  const [activeMetadataMap, setActiveMetadataMap] = useState<
    Record<string, ImageMetadataOverride>
  >({})
  const [activeLinkInfoMap, setActiveLinkInfoMap] = useState<
    Record<string, ImageLinkInfo>
  >({})
  const [activePdfMetadata, setActivePdfMetadata] = useState<
    CmrDocumentMetadata | undefined
  >()
  const [activePdfLinkInfo, setActivePdfLinkInfo] = useState<
    ImageLinkInfo | undefined
  >()

  const openImage = useCallback(
    (clickedUrl: string, allImageDocs: PreviewImageDoc[]) => {
      const directImages: DirectImageEntry[] = allImageDocs.map((doc) => ({
        src: mapFileUrlToSameOrigin(doc.url),
      }))
      const metadataMap: Record<string, ImageMetadataOverride> = {}
      const linkMap: Record<string, ImageLinkInfo> = {}
      for (const doc of allImageDocs) {
        const mappedSrc = mapFileUrlToSameOrigin(doc.url)
        if (doc.metadata) {
          metadataMap[mappedSrc] = {
            extractedMetadata: doc.metadata.extractedMetadata?.data,
            driverContext: toDocumentDriverContext(doc.metadata.driverContext),
            documentType: doc.metadata.extractedMetadata?.documentType,
            blurry: doc.metadata.extractedMetadata?.blurry,
            remarks: doc.metadata.extractedMetadata?.remarks,
          }
        }
        if (doc.messageId && doc.driverJid && doc.chatJid) {
          linkMap[mappedSrc] = {
            cmrMessageId: doc.messageId,
            cmrFileUrl: doc.url,
            cmrFileName: doc.url.split("/").pop() || "",
            chatJid: doc.chatJid,
            driverJid: doc.driverJid,
            unlinkInfo: doc.unlinkInfo,
          }
        }
      }
      setActiveDirectImages(directImages)
      setActiveMetadataMap(metadataMap)
      setActiveLinkInfoMap(linkMap)
      dispatch(
        updateImageGallery({
          visible: true,
          directImage: { src: mapFileUrlToSameOrigin(clickedUrl) },
          externalSource: PREVIEW_SOURCE,
        }),
      )
    },
    [dispatch],
  )

  const openPdf = useCallback(
    (
      pdfUrl: string,
      metadata?: CmrDocumentMetadata,
      reExtractInfo?: PreviewPdfReExtractInfo,
    ) => {
      setActivePdfMetadata(metadata)
      setActivePdfLinkInfo(
        reExtractInfo?.messageId &&
          reExtractInfo?.chatJid &&
          reExtractInfo?.driverJid
          ? {
              cmrMessageId: reExtractInfo.messageId,
              cmrFileUrl: pdfUrl,
              cmrFileName: pdfUrl.split("/").pop() || "",
              chatJid: reExtractInfo.chatJid,
              driverJid: reExtractInfo.driverJid,
              unlinkInfo: reExtractInfo.unlinkInfo,
            }
          : undefined,
      )
      const mappedUrl = mapFileUrlToSameOrigin(pdfUrl)
      dispatch(
        updatePDFViewer({
          visible: true,
          PDFMessage: {
            id: `cmr-pdf-${Date.now()}`,
            body: "",
            url: mappedUrl,
            file: true,
            from: "cmr-report@local",
            to: "cmr-report@local",
            type: "groupchat",
            markers: [],
            reactions: [],
            timestamp: new Date().toISOString(),
          } as unknown as Message,
          externalSource: PREVIEW_SOURCE,
        }),
      )
    },
    [dispatch],
  )

  const galleryProps = useMemo(
    () => ({
      messages: EMPTY_MESSAGES,
      directImages:
        activeDirectImages.length > 1 ? activeDirectImages : undefined,
      metadataOverrides:
        Object.keys(activeMetadataMap).length > 0
          ? activeMetadataMap
          : undefined,
      metadataOverride:
        activeDirectImages.length <= 1
          ? activeMetadataMap[activeDirectImages[0]?.src]?.extractedMetadata
          : undefined,
      driverContextOverride:
        activeDirectImages.length <= 1
          ? activeMetadataMap[activeDirectImages[0]?.src]?.driverContext
          : undefined,
      documentTypeOverride:
        activeDirectImages.length <= 1
          ? activeMetadataMap[activeDirectImages[0]?.src]?.documentType
          : undefined,
      linkInfoMap:
        Object.keys(activeLinkInfoMap).length > 0
          ? activeLinkInfoMap
          : undefined,
    }),
    [activeDirectImages, activeMetadataMap, activeLinkInfoMap],
  )

  const pdfProps = useMemo(
    () => ({
      metadataOverride: activePdfMetadata?.extractedMetadata?.data,
      driverContextOverride: toDocumentDriverContext(
        activePdfMetadata?.driverContext,
      ),
      documentTypeOverride: activePdfMetadata?.extractedMetadata?.documentType,
      blurryOverride: activePdfMetadata?.extractedMetadata?.blurry,
      remarksOverride: activePdfMetadata?.extractedMetadata?.remarks,
      linkInfo: activePdfLinkInfo,
    }),
    [activePdfMetadata, activePdfLinkInfo],
  )

  return {
    openImage,
    openPdf,
    galleryProps,
    pdfProps,
    galleryVisible,
    pdfVisible,
    /** True only when the most recent open was triggered through this hook. */
    ownsGallery,
    ownsPdf,
  }
}
